import Cookies from 'js-cookie'
import brightcoveIds from '../../../vod/js/config/brightcove-ids'
import { VOD_MESSAGE_TYPES, UPDATE_PREVIEW_PLAYER_PROPS, STV_COOKIES } from '../../../../constants'
import { initConvivaTracking } from '../../../../utils/dtmHelpers'

const env = window.parent.STV.environment
const isProduction = env === 'production'
const {
  PLAYING,
  CONTENT_HAS_ENDED,
  STARTED,
} = VOD_MESSAGE_TYPES

function hasOwnProperty(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key)
}

export default async function initPreviewPlayer({ selector, previewParams }) {
  initConvivaTracking(Cookies.get(STV_COOKIES.STV_USERID), isProduction)
  const userDigitalData = window?.parent?.digitalData.user
  const postMessageOrigin = window.location.origin
  const node = document.querySelector(selector)
  const {
    videoId,
    drmEnabled,
    title,
    genre,
    subGenre,
  } = previewParams

  if (!videoId) return

  const contentEnded = () => {
    window.parent.postMessage({
      type: CONTENT_HAS_ENDED,
    }, postMessageOrigin)
  }

  const {
    ACCOUNT_ID,
    PLAYER_ID,
    AD_CONFIG_ID,
    CONFIG_ID,
    POLICY_KEY,
  } = brightcoveIds(drmEnabled, isProduction)

  try {
    await onPageLoad()
    await insertPlayer()
    const playerInstance = await setupPlayer()

    addTrackingAnaltics(playerInstance)
    addPlayerListeners(playerInstance)
    playerInstance.controls(false)
    // player.play() might return a promise but it's not guaranteed crossbrowser.
    const promise = playerInstance.play()
    // let's play safe to ensure that if we do have a promise
    if (promise.then) {
      promise
        .then(() => {
          // if promise resolves, show the video
        })
        .catch((e) => {
          console.error('Error playing preview video:', e) // eslint-disable-line no-console
        })
    }
  } catch (error) {
    console.error('Error initializing preview player:', error) // eslint-disable-line no-console
    contentEnded()
  }

  async function insertPlayer() {
    const player = node.querySelector('video')
    player.setAttribute('data-account', ACCOUNT_ID)
    player.setAttribute('data-player', PLAYER_ID)

    let playerLoadTimeout = null

    const playerRequest = new Promise((resolve) => {
      const playerScriptUrl = `//players.brightcove.net/${ACCOUNT_ID}/${PLAYER_ID}_default/index.min.js`
      const playerScriptEl = document.createElement('script')
      playerScriptEl.src = playerScriptUrl
      playerScriptEl.onload = () => {
        clearTimeout(playerLoadTimeout)
        resolve()
      }
      document.body.appendChild(playerScriptEl)
    })

    const timeout = new Promise((resolve) => {
      playerLoadTimeout = setTimeout(() => {
        const error = {
          success: false,
          message: `Timed out after 5000ms`,
        }
        reportError(error)
        resolve(error)
      }, 7500)
    })

    return Promise.race([playerRequest, timeout])
  }

  /**
   * Initialise the BrightCove player
   */
  async function setupPlayer() {
    // create the player
    const videoEl = node.querySelector('video')
    const player = window.bc(videoEl)
    player.debug(!isProduction)
    player.muted(true)
    player.autoplay(true)

    const playerReady = new Promise((resolve) => {
      player.ready(resolve)
    })

    const [, json] = await Promise.all([playerReady, getVideoFromBrightcove()])
    if (json[0] && hasOwnProperty(json[0], 'error_code')) {
      console.error('Error getting preview video from bc:', json[0]) // eslint-disable-line no-console
      contentEnded()
      return {}
    }
    // turn the API response into a format the player can use
    const videoObject = player.catalog.transformVideoResponse(json)
    player.catalog.load(videoObject)

    return player
  }

  async function getVideoFromBrightcove() {
    // hardcoding video id for the moment
    const url = [
      `https://edge.api.brightcove.com/playback/v1/accounts/${ACCOUNT_ID}/videos/${videoId}`,
      `?ad_config_id=${AD_CONFIG_ID}`,
      `&config_id=${CONFIG_ID}`,
    ].join('')

    try {
      const response = await fetch(url, {
        headers: {
          Accept: `application/json;pk=${POLICY_KEY}`,
        },
      })
      const json = await response.json()
      return json
    } catch (error) {
      reportError(error)
      return error
    }
  }
  function addTrackingAnaltics(player) {
  // Create complete metadata object for Conviva with all required fields
    const convivaMetadata = {
      'c3.cm.contentType': 'vc11',
      'c3.cm.channel': 'stv',
      'c3.cm.seriesName': title,
      'c3.cm.id': videoId || 'matID_not_set',
      'c3.cm.showTitle': title,
      'c3.cm.genreList': subGenre || 'undefined',
      'c3.cm.genre': genre,
      [window?.Conviva?.Constants.PLAYER_NAME]: 'player',
      [window?.Conviva?.Constants.VIEWER_ID]: userDigitalData.userId,
      [window?.Conviva?.Constants?.APPLICATION_VERSION]: '1.1.0',
      broadcastRegion: userDigitalData.broadcastRegion,
      user_subs_status: userDigitalData.subscriptionStatus,
      sub_genre: subGenre || 'undefined',
    }

    // add the metadata to setContentInfo when load
    player.on('loadedmetadata', () => {
      window?.ConvivaVideoAnalytics?.setContentInfo(convivaMetadata)
    })
  }
  function addPlayerListeners(player) {
    // add ended event listener
    player.on('ended', () => {
      contentEnded()
    })

    // add playing event listener
    player.on('playing', () => {
      window.parent.postMessage({
        type: PLAYING,
      }, postMessageOrigin)

      // once playing transition to next slide on error
      player.off('error', () => {
        contentEnded()
      })
      player.on('error', () => {
        contentEnded()
      })
    })

    player.on('play', () => {
      window.parent.postMessage({
        type: STARTED,
      }, postMessageOrigin)

      // once playing transition to next slide on error
      player.off('error', () => {
        contentEnded()
      })
      player.on('error', () => {
        contentEnded()
      })
    })

    player.on('error', () => {
      contentEnded()
    })

    window.parent.addEventListener('message', (message) => {
      if (message.data.type === UPDATE_PREVIEW_PLAYER_PROPS) {
        const data = message.data.newData

        if (hasOwnProperty(data, 'muted')) {
          player.muted(data.muted)
        }
        if (hasOwnProperty(data, 'paused')) {
          // eslint-disable-next-line no-unused-expressions
          data.paused ? player.pause() : player.play()
        }
      }
    })
  }
}

function onPageLoad() {
  return new Promise((resolve) => {
    document.addEventListener('DOMContentLoaded', resolve)
  })
}
